/**
 * Database Mutations
 *
 * Create, update, and delete operations for spaces, folders, lists, and items.
 */

import { supabase } from '@/lib/supabaseClient';
import { withRetry } from '@/utils/retry';
import type { Project, Document, Chat, VoiceChannel, Spreadsheet, Presentation } from './types';
import { convertSpaceFromSupabase, getRootProjectId, getNextOrderIndex, formatDatabaseError } from './helpers';
import { handleError } from '@/utils/errorHandler';
import { invalidateCache } from '@/utils/queryCache';

// Create functions
export async function createProject(name: string, description?: string, orgId?: string, parentId?: string): Promise<{ id: string; name: string }> {
  const orderIndex = await getNextOrderIndex(parentId || null, 'project');

  // Use RPC to atomically create space + add creator as member (avoids RLS race condition)
  const { data, error } = await withRetry(() =>
    supabase.rpc('create_space_with_membership', {
      p_type: 'project',
      p_parent_id: parentId || null,
      p_name: name,
      p_description: description || null,
      p_order_index: orderIndex,
      p_org_id: orgId || null,
    })
  );

  if (error) {
    throw new Error(formatDatabaseError(error));
  }

  invalidateCache('getAllSpaces');
  invalidateCache('getAllFolders');
  invalidateCache('getAllLists');
  invalidateCache('getAllSpaceItems');
  return { id: data.id, name: data.name };
}

export async function createFolder(
  parentId: string,
  name: string,
  orderIndex?: number
): Promise<{ id: string; name: string; project_id: string }> {
  // Validate parent exists and get org_id
  const { data: parent } = await withRetry(() => supabase.from('spaces').select('id, type, org_id').eq('id', parentId).single());

  if (!parent) {
    throw new Error(`Parent ${parentId} does not exist`);
  }

  // Check for duplicate name
  const { data: existing } = await withRetry(() =>
    supabase
      .from('spaces')
      .select('id')
      .eq('type', 'folder')
      .eq('parent_id', parentId)
      .eq('name', name)
      .single()
  );

  if (existing) {
    throw new Error(`A folder with name "${name}" already exists in this parent`);
  }

  // If orderIndex is 0 (inserting at top), increment all other folders' order_index
  if (orderIndex === 0) {
    await withRetry(() =>
      supabase.rpc('increment_order_indexes', {
        p_parent_id: parentId,
        p_type: 'folder',
        p_from_index: 0,
      })
    );
  }

  const finalOrderIndex = orderIndex !== undefined ? orderIndex : await getNextOrderIndex(parentId, 'folder');
  const rootProjectId = await getRootProjectId(parentId);
  if (!rootProjectId) {
    throw new Error('Cannot determine root project for folder creation');
  }

  const { data: folder, error } = await withRetry(() =>
    supabase
      .from('spaces')
      .insert({
        type: 'folder',
        parent_id: parentId,
        project_id: rootProjectId,
        name,
        order_index: finalOrderIndex,
        org_id: parent.org_id,
      })
      .select('id, name')
      .single()
  );

  if (error || !folder) throw new Error(formatDatabaseError(error || { message: 'Failed to create folder' }));
  invalidateCache('getAllFolders');
  invalidateCache('getAllSpaceItems');
  return { id: folder.id, name: folder.name, project_id: rootProjectId };
}

export async function createList(
  parentId: string,
  name: string,
  orderIndex?: number
): Promise<{ id: string; name: string; project_id: string; folder_id: string | null }> {
  // Validate parent exists and get org_id
  const { data: parent } = await withRetry(() => supabase.from('spaces').select('id, type, parent_id, org_id').eq('id', parentId).single());

  if (!parent) {
    throw new Error(`Parent ${parentId} does not exist`);
  }

  const rootProjectId = await getRootProjectId(parentId);
  if (!rootProjectId) {
    throw new Error('Cannot determine root project for list creation');
  }

  // Check for duplicate name
  const { data: existing } = await withRetry(() =>
    supabase
      .from('spaces')
      .select('id')
      .eq('type', 'list')
      .eq('parent_id', parentId)
      .eq('name', name)
      .single()
  );

  if (existing) {
    throw new Error(`A list with name "${name}" already exists in this parent`);
  }

  // If orderIndex is 0 (inserting at top), increment all other lists' order_index
  if (orderIndex === 0) {
    await withRetry(() =>
      supabase.rpc('increment_order_indexes', {
        p_parent_id: parentId,
        p_type: 'list',
        p_from_index: 0,
      })
    );
  }

  const finalOrderIndex = orderIndex !== undefined ? orderIndex : await getNextOrderIndex(parentId, 'list');
  const folderId = parent.type === 'folder' ? parent.id : null;

  const { data: list, error } = await withRetry(() =>
    supabase
      .from('spaces')
      .insert({
        type: 'list',
        parent_id: parentId,
        project_id: rootProjectId,
        name,
        order_index: finalOrderIndex,
        org_id: parent.org_id,
      })
      .select('id, name')
      .single()
  );

  if (error || !list) throw new Error(formatDatabaseError(error || { message: 'Failed to create list' }));
  invalidateCache('getAllLists');
  invalidateCache('getAllSpaceItems');
  return { id: list.id, name: list.name, project_id: rootProjectId, folder_id: folderId };
}

async function createSpaceItem(
  type: 'document' | 'chat' | 'voice_channel' | 'spreadsheet' | 'presentation',
  containerId: string,
  name: string,
  content: string | unknown[] | Record<string, unknown> | null = null,
  orderIndex?: number
): Promise<Project> {
  // Validate container exists and get org_id
  const { data: container } = await withRetry(() => supabase.from('spaces').select('id, type, org_id').eq('id', containerId).single());

  if (!container) {
    throw new Error(`Container with id ${containerId} does not exist`);
  }

  const rootProjectId = await getRootProjectId(containerId);
  if (!rootProjectId) {
    throw new Error(`Cannot determine root project for ${type} creation`);
  }

  let finalOrderIndex = orderIndex;
  if (finalOrderIndex === 0) {
    await withRetry(() =>
      supabase.rpc('increment_order_indexes', {
        p_parent_id: containerId,
        p_type: type,
        p_from_index: 0,
      })
    );
  } else {
    finalOrderIndex = await getNextOrderIndex(containerId, type);
  }

  const insertData: Record<string, unknown> = {
    type,
    parent_id: containerId,
    project_id: rootProjectId,
    name,
    order_index: finalOrderIndex,
    org_id: container.org_id,
  };

  if (content !== null) {
    insertData.content = content;
  } else {
    // Default content based on type
    if (type === 'document') insertData.content = '';
    else if (type === 'chat' || type === 'presentation') insertData.content = [];
    else if (type === 'voice_channel' || type === 'spreadsheet') insertData.content = {};
  }

  const { data, error } = await withRetry(() =>
    supabase.from('spaces').insert(insertData).select().single()
  );

  if (error || !data) {
    throw new Error(formatDatabaseError(error || { message: `Failed to create ${type}` }));
  }

  invalidateCache('getAllSpaceItems');
  return convertSpaceFromSupabase(data);
}

export async function createDocument(
  projectId: string,
  folderId: string | null,
  name: string,
  orderIndex?: number
): Promise<Document & { id: string }> {
  const doc = await createSpaceItem('document', projectId, name, '', orderIndex);
  return doc as Document & { id: string };
}

export async function createChat(
  spaceId: string,
  folderId: string | null,
  name: string,
  orderIndex?: number
): Promise<Chat & { id: string }> {
  const chat = await createSpaceItem('chat', spaceId, name, [], orderIndex);
  return chat as Chat & { id: string };
}

export async function createVoiceChannel(
  spaceId: string,
  folderId: string | null,
  name: string,
  orderIndex?: number
): Promise<VoiceChannel & { id: string }> {
  const channel = await createSpaceItem('voice_channel', spaceId, name, {}, orderIndex);
  return channel as VoiceChannel & { id: string };
}

export async function createSpreadsheet(
  spaceId: string,
  folderId: string | null,
  name: string,
  orderIndex?: number
): Promise<Spreadsheet & { id: string }> {
  const sheet = await createSpaceItem('spreadsheet', spaceId, name, {}, orderIndex);
  return sheet as Spreadsheet & { id: string };
}

export async function createPresentation(
  spaceId: string,
  folderId: string | null,
  name: string,
  orderIndex?: number
): Promise<Presentation & { id: string }> {
  const pres = await createSpaceItem('presentation', spaceId, name, [], orderIndex);
  return pres as Presentation & { id: string };
}

export async function createCategory(name: string, orgId?: string): Promise<{ id: string; name: string }> {
  const orderIndex = await getNextOrderIndex(null, 'category');

  const { data, error } = await withRetry(() =>
    supabase.rpc('create_space_with_membership', {
      p_type: 'category',
      p_parent_id: null,
      p_name: name,
      p_description: null,
      p_order_index: orderIndex,
      p_org_id: orgId || null,
    })
  );

  if (error) {
    throw new Error(formatDatabaseError(error));
  }

  invalidateCache('getAllSpaces');
  return { id: data.id, name: data.name };
}

// Update functions
async function updateSpaceItem(id: string, updates: { name?: string; content?: string | unknown[] | Record<string, unknown> }): Promise<Project | null> {
  // Skip if id is a temp ID (optimistic update) - wait for real ID
  if (id.startsWith('temp-')) {
    handleError(new Error('Cannot update space item with temp ID'), { level: 'warning', message: 'Cannot update space item with temp ID', operation: 'updateSpaceItem' });
    return null;
  }

  const updateData: Record<string, unknown> = {};
  if (updates.name !== undefined) updateData.name = updates.name;
  if (updates.content !== undefined) updateData.content = updates.content;

  const { data, error } = await withRetry(() =>
    supabase.from('spaces').update(updateData).eq('id', id).select().single()
  );

  if (error || !data) return null;
  return convertSpaceFromSupabase(data);
}

export async function updateDocument(
  id: string,
  updates: Partial<Pick<Document, 'name' | 'content'>>
): Promise<(Document & { id: string }) | null> {
  const doc = await updateSpaceItem(id, updates);
  return doc ? (doc as Document & { id: string }) : null;
}

export async function updateChat(
  id: string,
  updates: Partial<Pick<Chat, 'name' | 'content'>>
): Promise<(Chat & { id: string }) | null> {
  const chat = await updateSpaceItem(id, updates);
  return chat ? (chat as Chat & { id: string }) : null;
}

export async function updateSpreadsheet(
  id: string,
  updates: Partial<Pick<Spreadsheet, 'name' | 'content'>>
): Promise<(Spreadsheet & { id: string }) | null> {
  const sheet = await updateSpaceItem(id, updates);
  return sheet ? (sheet as Spreadsheet & { id: string }) : null;
}

export async function updatePresentation(
  id: string,
  updates: Partial<Pick<Presentation, 'name' | 'content'>>
): Promise<(Presentation & { id: string }) | null> {
  const pres = await updateSpaceItem(id, updates);
  return pres ? (pres as Presentation & { id: string }) : null;
}

export async function updateVoiceChannel(
  id: string,
  updates: Partial<Pick<VoiceChannel, 'name' | 'content'>>
): Promise<(VoiceChannel & { id: string }) | null> {
  const channel = await updateSpaceItem(id, updates);
  return channel ? (channel as VoiceChannel & { id: string }) : null;
}

// Rename, move, archive, duplicate functions
export async function renameSpace(id: string, name: string): Promise<{ id: string; name: string } | null> {
  // Check if another item with the same name already exists in the same parent
  const { data: item } = await withRetry(() => supabase.from('spaces').select('id, type, parent_id').eq('id', id).single());

  if (!item) {
    throw new Error(`Item ${id} does not exist`);
  }

  const { data: existing } = await withRetry(() =>
    supabase
      .from('spaces')
      .select('id')
      .eq('type', item.type)
      .eq('parent_id', item.parent_id)
      .eq('name', name)
      .neq('id', id)
      .single()
  );

  if (existing) {
    throw new Error(formatDatabaseError({ message: `An item with name "${name}" already exists in this location` }));
  }

  const { data: space, error } = await withRetry(() =>
    supabase
      .from('spaces')
      .update({ name })
      .eq('id', id)
      .select('id, name')
      .single()
  );

  if (error || !space) return null;
  invalidateCache('getAllSpaces');
  invalidateCache('getAllFolders');
  invalidateCache('getAllLists');
  invalidateCache('getAllSpaceItems');
  return { id: space.id, name: space.name };
}

export async function moveSpace(id: string, newParentId: string): Promise<{ id: string } | null> {
  // Get item and new parent
  const { data: item } = await withRetry(() => supabase.from('spaces').select('id, name, type, parent_id').eq('id', id).single());

  if (!item) {
    throw new Error(`Item ${id} does not exist`);
  }

  const { data: newParent } = await withRetry(() => supabase.from('spaces').select('id').eq('id', newParentId).single());

  if (!newParent) {
    throw new Error(`New parent ${newParentId} does not exist`);
  }

  // Prevent moving an item into itself or its descendants
  if (id === newParentId) {
    throw new Error('Cannot move an item into itself');
  }

  // Check for cycles
  let currentParentId: string | null = newParentId;
  const visited = new Set<string>();
  while (currentParentId && !visited.has(currentParentId)) {
    if (currentParentId === id) {
      throw new Error('Cannot move an item into its own descendants');
    }
    visited.add(currentParentId);
    const { data: parent }: { data: { parent_id: string | null } | null } = await withRetry(() =>
      supabase
        .from('spaces')
        .select('parent_id')
        .eq('id', currentParentId!)
        .single()
    );
    currentParentId = parent?.parent_id || null;
  }

  // Check for duplicate name
  const { data: existing } = await withRetry(() =>
    supabase
      .from('spaces')
      .select('id')
      .eq('type', item.type)
      .eq('parent_id', newParentId)
      .eq('name', item.name)
      .single()
  );

  if (existing) {
    throw new Error(formatDatabaseError({ message: `An item with name "${item.name}" already exists in the destination` }));
  }

  const orderIndex = await getNextOrderIndex(newParentId, item.type);

  const { data: space, error } = await withRetry(() =>
    supabase
      .from('spaces')
      .update({ parent_id: newParentId, order_index: orderIndex })
      .eq('id', id)
      .select('id')
      .single()
  );

  if (error || !space) return null;
  invalidateCache('getAllSpaces');
  invalidateCache('getAllFolders');
  invalidateCache('getAllLists');
  invalidateCache('getAllSpaceItems');
  return { id: space.id };
}

// Atomic recursive delete via RPC (runs in a single transaction)
async function _deleteRecursive(itemId: string): Promise<void> {
  const { error } = await withRetry(() =>
    supabase.rpc('delete_space_recursive', { p_space_id: itemId })
  );
  if (error) throw error;
}

export async function archiveSpace(id: string): Promise<boolean> {
  try {
    // Recursively archive this space and all its children
    await archiveRecursive(id, true);
    invalidateCache('getAllSpaces');
    invalidateCache('getAllFolders');
    invalidateCache('getAllLists');
    invalidateCache('getAllSpaceItems');
    return true;
  } catch (error) {
    handleError(error, { level: 'error', message: 'Error archiving space' });
    return false;
  }
}

export async function unarchiveSpace(id: string): Promise<boolean> {
  try {
    // Only unarchive the specific space, not its children.
    // Children may have been archived independently before the parent,
    // so recursive unarchive would incorrectly restore them.
    const { error } = await withRetry(() =>
      supabase.from('spaces').update({ archived: false }).eq('id', id)
    );
    if (error) throw error;
    invalidateCache('getAllSpaces');
    invalidateCache('getAllFolders');
    invalidateCache('getAllLists');
    invalidateCache('getAllSpaceItems');
    return true;
  } catch (error) {
    handleError(error, { level: 'error', message: 'Error unarchiving space' });
    return false;
  }
}

async function archiveRecursive(id: string, archived: boolean): Promise<void> {
  // Set archived flag on this space
  const { error } = await withRetry(() =>
    supabase.from('spaces').update({ archived }).eq('id', id)
  );
  if (error) throw error;

  // Find and archive all direct children
  const { data: children } = await withRetry(() =>
    supabase.from('spaces').select('id').eq('parent_id', id)
  );
  if (children && children.length > 0) {
    await Promise.all(children.map((child) => archiveRecursive(child.id, archived)));
  }
}

export async function duplicateSpace(id: string, name?: string): Promise<{ id: string; name: string } | null> {
  // Get item to duplicate
  const { data: item } = await withRetry(() => supabase.from('spaces').select('*').eq('id', id).single());

  if (!item) {
    throw new Error(`Item ${id} does not exist`);
  }

  const newName = name || `${item.name} (copy)`;

  // Check for duplicate name
  const { data: existing } = await withRetry(() =>
    supabase
      .from('spaces')
      .select('id')
      .eq('type', item.type)
      .eq('parent_id', item.parent_id)
      .eq('name', newName)
      .single()
  );

  if (existing) {
    throw new Error(formatDatabaseError({ message: `An item with name "${newName}" already exists in this location` }));
  }

  // Create duplicate (simplified - doesn't duplicate children recursively)
  const orderIndex = await getNextOrderIndex(item.parent_id, item.type);
  const { data: duplicate, error } = await withRetry(() =>
    supabase
      .from('spaces')
      .insert({
        type: item.type,
        parent_id: item.parent_id,
        project_id: item.project_id,
        name: newName,
        description: item.description,
        content: item.content ? JSON.parse(JSON.stringify(item.content)) : null,
        order_index: orderIndex,
        org_id: item.org_id,
      })
      .select('id, name')
      .single()
  );

  if (error || !duplicate) return null;
  invalidateCache('getAllSpaces');
  invalidateCache('getAllFolders');
  invalidateCache('getAllLists');
  invalidateCache('getAllSpaceItems');
  return { id: duplicate.id, name: duplicate.name };
}

export async function deleteSpace(id: string): Promise<boolean> {
  try {
    // Check if there are children
    const { data: children } = await withRetry(() => supabase.from('spaces').select('id').eq('parent_id', id).is('archived', false).limit(1));

    if (children && children.length > 0) {
      throw new Error(formatDatabaseError({ message: `Cannot delete space ${id}: it has children` }));
    }

    await withRetry(() => supabase.from('spaces').delete().eq('id', id));
    invalidateCache('getAllSpaces');
    invalidateCache('getAllFolders');
    invalidateCache('getAllLists');
    invalidateCache('getAllSpaceItems');
    return true;
  } catch (error) {
    handleError(error, { level: 'error', message: 'Error deleting space' });
    return false;
  }
}

// Update order of multiple items
export async function updateOrder(items: Array<{ id: string; orderIndex: number }>): Promise<boolean> {
  try {
    // Update all items in parallel
    const updates = items.map((item) =>
      withRetry(() => supabase.from('spaces').update({ order_index: item.orderIndex }).eq('id', item.id))
    );
    await Promise.all(updates);
    return true;
  } catch (error) {
    handleError(error, { level: 'error', message: 'Error updating order' });
    return false;
  }
}
